/**
 * Générateur — Automatisme : multiplier et diviser par 10, 100, 1 000.
 * Attendu ID : 1094 | Niveau : 4ème | Domaine : Opérations sur les nombres relatifs
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 9, ligne 635 :
 * « Multiplier et diviser par 10, 100, 1 000. »), mot pour mot la ligne de 5e
 * (l. 556) et l'entretien des deux automatismes de 6e.
 *
 * ── WRAPPER de gen_1043 (5e), lui-même wrapper des gens 6e 1008 + 1009 ────
 * Même chaîne de délégation que 1043 : zéro duplication, une seule
 * maintenance. Le payload est re-badgé (niveau 4eme, attendu 1094) ; les
 * cartes, formats, generator_keys (produits_* / quotients_*), invariants
 * (glissement de chaîne sans flottant, item « × 1 » anti-routine,
 * distracteurs nommés) sont ceux des gens 6e. Coups de pouce et solveurs
 * réutilisent l'entrée 1043.
 *
 * Périmètre observé MathsMentales (page 4e, activités 8NE4 et 6NE11 : un
 * décimal à un chiffre significatif × ou ÷ 10, 100, 1 000 ; S. Cogez,
 * Apache 2.0, relevé 2026-09-12) : strictement couvert par les gens 6e, qui
 * vont plus loin (plusieurs chiffres significatifs, × 1). Rien à ajouter.
 *
 * Hors périmètre, et pourquoi : les nombres NÉGATIFS (−3,5 × 100). Le
 * programme range cet automatisme sous « Opérations sur les nombres
 * relatifs », mais la ligne ne parle pas de signe et le produit d'un
 * relatif par un positif est un OBJECTIF de 4e (l. 640 : « d'abord dans le
 * cas où un seul des facteurs est négatif »), pas un automatisme.
 */
import { generate as genererDecalage, CATEGORIES as CAT_DECALAGE } from '../../5eme/operations/gen_1043';
import { retirerCodesErreur } from '../../../utils';

const ATTENDU_ID = 1094;

export const CATEGORIES = CAT_DECALAGE;

// ─── Codes d'erreur (contenu.erreurs, codes communs du catalogue) ─────────

const RANGS = 'nombre_de_rangs_faux';
const SENS = 'sens_du_glissement_inverse';
const ROUTINE = 'routine_appliquee';
const PUISSANCES = ['10', '100', '1000'];
const LETTRES = 'ABCDEFGH';

/** Décale l'écriture brute « 12,6 » de k rangs (k > 0 : × 10^k), comme le glissement des gens 6e ; écriture canonique. */
const decaler = (s, k) => {
    const [ent, dec = ''] = s.split(',');
    let chiffres = ent + dec;
    let virgule = ent.length + k;
    if (virgule < 1) { chiffres = '0'.repeat(1 - virgule) + chiffres; virgule = 1; }
    chiffres = chiffres.padEnd(virgule, '0');
    const e = chiffres.slice(0, virgule).replace(/^0+(?=\d)/, '');
    const d = chiffres.slice(virgule).replace(/0+$/, '');
    return d ? `${e},${d}` : e;
};

/** Les rangs d'un glissement trop court ou trop long pour 10^q (familles glissement_insuffisant et glissement_excessif). */
const rangsFaux = (q) => [...Array(q - 1).keys()].map((i) => i + 1).concat(q + 1, q + 2);

/** Le code d'un résultat écrit w pour A × 10^q (fois) ou A ÷ 10^q, d'après les familles de 1008 et 1009 ; null sinon. */
const codeDecalage = (fois, A, q, w) => {
    const v = decaler(w, 0);
    const vers = (j) => decaler(A, fois ? j : -j);
    if (q === 0) return [1, 2, 3].some((k) => v === decaler(A, k) || v === decaler(A, -k)) ? ROUTINE : null;
    if (fois && A.includes(',') && /,\d*0$/.test(w) && v === decaler(A, 0)) return 'ajout_zero';
    if ([1, 2, 3, 4, 5].some((j) => v === vers(-j))) return SENS;
    return rangsFaux(q).some((j) => v === vers(j)) ? RANGS : null;
};

/** Les résultats faux qu'une saisie sait nommer (ajout_zero exclu : la tolérance numérique le confondrait avec le nombre recopié). */
const resultatsFaux = (fois, A, q) => {
    const vers = (j) => decaler(A, fois ? j : -j);
    if (q === 0) return [1, 2, 3].flatMap((k) => [[decaler(A, k), ROUTINE], [decaler(A, -k), ROUTINE]]);
    return [1, 2, 3, 4, 5].map((j) => [vers(-j), SENS]).concat(rangsFaux(q).map((j) => [vers(j), RANGS]));
};

/** Une ligne « 12,6 × 1 000 = {{12600}} » ou « 0,45 × {{100}} = 45 », sans les espaces de milliers ; null sinon. */
const lireLigne = (ligne) => {
    const m = ligne.match(/^(.+) ([×÷]) (.+) = (.+)$/);
    const sans = (s) => s.replace(/\{\{|\}\}|\s/g, '');
    const q = m ? ['1', ...PUISSANCES].indexOf(sans(m[3])) : -1;
    return q < 0 ? null : { fois: m[2] === '×', A: sans(m[1]), q, M: sans(m[3]), trouSurM: m[3].includes('{{'), R: sans(m[4]) };
};

/** Le code d'une valeur posée dans le trou d'une ligne : puissance voisine sur le multiplicateur, glissement sur le résultat. */
const codeTrou = (l, w) => (l.trouSurM ? (PUISSANCES.includes(w) && w !== l.M ? RANGS : null) : codeDecalage(l.fois, l.A, l.q, w));

/** Les erreurs du format tiré, recalculées depuis le payload de 1008 ou 1009 (lu, jamais modifié). */
const erreursDecalage = (payload) => {
    const cle = payload._meta?.generatorKey || '';
    const c = payload.contenu || {};
    if (cle.endsWith('_qcm_unique')) {
        const { a, q } = payload._meta.params || {};
        const juste = c.choix[LETTRES.indexOf(c.bonne_reponse)];
        return c.choix.filter((ch) => ch !== juste)
            .map((ch) => ({ code: codeDecalage(cle.startsWith('produits'), a, q, ch.replace(/\s/g, '')), valeur: ch })).filter((e) => e.code);
    }
    if (cle.endsWith('_tableau_vf')) {
        const table = (payload.elements_additionnels?.elements || []).find((e) => e.type === 'table')?.config;
        return (table?.lignes || []).map((ligne, i) => {
            const l = table.bonnesReponses[i] === 1 ? lireLigne(ligne) : null;
            return { code: l && codeDecalage(l.fois, l.A, l.q, l.R), ligne };
        }).filter((e) => e.code);
    }
    if (!c.phrase) return [];
    return c.phrase.split('\n').flatMap((ligne, trou) => {
        const l = lireLigne(ligne);
        if (!l) return [];
        if (c.mode === 'drag') return (c.distracteurs || []).map((d) => ({ code: codeTrou(l, d), trou, valeur: d })).filter((e) => e.code);
        const faux = l.trouSurM ? PUISSANCES.filter((p) => p !== l.M).map((p) => [p, RANGS]) : resultatsFaux(l.fois, l.A, l.q);
        return faux.filter(([v], i) => faux.findIndex(([w]) => w === v) === i).map(([valeur, code]) => ({ code, trou, valeur }));
    });
};

// Relecture du 04/10/2026 (docs/SFD_CODES_ERREUR.md) : on ne garde un code que sur les cartes où le juge
// l'a lu. Ces vrai/faux ne lui ont jamais été montrés : le choix reste proposé, il part sans nom.
const CODES_RETIRES = {
    produits_tableau_vf: ['nombre_de_rangs_faux'],
    quotients_tableau_vf: ['nombre_de_rangs_faux'],
};

export const generate = (typeExercice) => {
    const payload = genererDecalage(typeExercice);
    payload.niveau = '4eme';
    payload.attendu = ATTENDU_ID;
    // Classer : aucun code (la correction n'en cherche pas)
    if (!(payload._meta?.generatorKey || '').endsWith('_classer')) payload.contenu = { ...payload.contenu, erreurs: erreursDecalage(payload) };
    return retirerCodesErreur(payload, CODES_RETIRES[payload._meta?.generatorKey]);
};
